import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { buildSkillProgress, type AssessmentRecord } from '@/lib/skillProgressAggregation'

export type SkillOutcome = 'demonstrated' | 'needs_practice' | 'not_assessed' | 'taught'

export interface DrivingSkill {
  id: string
  code: string
  name: string
  category: string | null
  sortOrder: number
}

export interface SkillAssessment {
  id: string
  skillId: string
  outcome: SkillOutcome
  observation: string | null
  updatedAt: string
}

interface UpsertResult {
  ok: boolean
  code?: string
}

/** The fixed, system-seeded catalog of practical skills (migration 057). Read-only. */
export function useDrivingSkills(): { skills: DrivingSkill[]; loading: boolean; error: string | null } {
  const { user } = useAuth()
  const [skills, setSkills] = useState<DrivingSkill[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      const { data, error: fetchErr } = await supabase
        .from('driving_skills')
        .select('id, code, name, category, sort_order')
        .order('sort_order', { ascending: true })
      if (cancelled) return
      if (fetchErr) { setError('Could not load the skill list.'); setLoading(false); return }
      setSkills((data ?? []).map((row: any) => ({
        id: row.id, code: row.code, name: row.name, category: row.category, sortOrder: row.sort_order,
      })))
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [user])

  return { skills, loading, error }
}

/**
 * Skill assessments recorded for one specific lesson. Authorization for writes is
 * re-verified entirely server-side inside upsert_lesson_skill_assessment() (migration 057)
 * from lessonId alone — this hook never asserts ownership itself.
 */
export function useLessonAssessments(lessonId: string | undefined) {
  const { user } = useAuth()
  const [assessments, setAssessments] = useState<SkillAssessment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingSkillId, setSavingSkillId] = useState<string | null>(null)

  const fetchAssessments = useCallback(async () => {
    if (!user || !lessonId) return
    setLoading(true)
    setError(null)
    const { data, error: fetchErr } = await supabase
      .from('lesson_skill_assessments')
      .select('id, skill_id, outcome, observation, updated_at')
      .eq('lesson_id', lessonId)

    if (fetchErr) { setError('Could not load assessments for this lesson.'); setLoading(false); return }
    setAssessments((data ?? []).map((row: any) => ({
      id: row.id, skillId: row.skill_id, outcome: row.outcome, observation: row.observation, updatedAt: row.updated_at,
    })))
    setLoading(false)
  }, [user, lessonId])

  useEffect(() => { fetchAssessments() }, [fetchAssessments])

  const saveAssessment = useCallback(async (
    skillId: string, outcome: SkillOutcome, observation: string | null,
  ): Promise<UpsertResult> => {
    if (!lessonId) return { ok: false, code: 'no_lesson' }
    setSavingSkillId(skillId)
    try {
      const { data, error: rpcError } = await supabase.rpc('upsert_lesson_skill_assessment', {
        p_lesson_id: lessonId,
        p_skill_id: skillId,
        p_outcome: outcome,
        p_observation: observation ?? undefined,
      })
      if (rpcError) return { ok: false, code: 'internal_error' }
      const result = data as { ok: boolean; code?: string } | null
      if (result?.ok) await fetchAssessments()
      return { ok: result?.ok ?? false, code: result?.code }
    } finally {
      setSavingSkillId(null)
    }
  }, [lessonId, fetchAssessments])

  return { assessments, loading, error, savingSkillId, saveAssessment, refresh: fetchAssessments }
}

export type { SkillProgressEntry } from '@/lib/skillProgressAggregation'

/** Cross-lesson progress review for one student: latest + previous outcome per skill,
 *  across every lesson this instructor is authorized to see for them. Computed live from
 *  the authoritative rows on every call — never a separately-stored summary. The actual
 *  "pick latest/previous per skill" logic lives in the pure, unit-tested
 *  buildSkillProgress() (src/lib/skillProgressAggregation.ts) — this hook only fetches and
 *  shapes the rows. */
export function useStudentSkillProgress(studentId: string | undefined) {
  const { user } = useAuth()
  const [entries, setEntries] = useState<ReturnType<typeof buildSkillProgress>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProgress = useCallback(async () => {
    if (!user || !studentId) return
    setLoading(true)
    setError(null)

    const { data, error: fetchErr } = await supabase
      .from('lesson_skill_assessments')
      .select('skill_id, outcome, observation, updated_at, lesson_id, driving_skills(name, category), driving_lessons(scheduled_at)')
      .eq('student_id', studentId)
      .order('updated_at', { ascending: false })

    if (fetchErr) { setError('Could not load skill progress.'); setLoading(false); return }

    const records: AssessmentRecord[] = (data ?? []).map((row: any) => ({
      skillId: row.skill_id,
      skillName: row.driving_skills?.name ?? 'Unknown skill',
      skillCategory: row.driving_skills?.category ?? null,
      outcome: row.outcome,
      observation: row.observation,
      updatedAt: row.updated_at,
      lessonId: row.lesson_id ?? null,
      lessonScheduledAt: row.driving_lessons?.scheduled_at ?? null,
    }))

    setEntries(buildSkillProgress(records))
    setLoading(false)
  }, [user, studentId])

  useEffect(() => { fetchProgress() }, [fetchProgress])

  return { entries, loading, error, refresh: fetchProgress }
}
